<template>
    <section class="store-section" ref="startForm" :style="{'scroll-margin-top':headerHeight}">
        <div>
            <h3 class="store-title">Checkout</h3>
        </div>

        <div class="store-wrap">
            <div v-if="!isSent" class="checkout-form-container">
                <div class="fields-wrap">
                    <div class="field-box">
                        <label class="field-label">First Name <span class="field-required">*</span></label>
                        <div class="field-element">
                            <input type="text" style="width: 100%;" v-model="form.first_name" />
                            <input type="hidden" name="secret_name" v-model="form.secret_name" />
                            <div class="error-messages">{{ messageFieldError('first_name') }}</div>
                        </div>
                    </div>

                    <div class="field-box">
                        <label class="field-label">Last Name <span class="field-required">*</span></label>
                        <div class="field-element">
                            <input type="text" style="width: 100%;" v-model="form.last_name" />
                            <div class="error-messages">{{ messageFieldError('last_name') }}</div>
                        </div>
                    </div>

                    <div class="field-box">
                        <label class="field-label">Address <span class="field-required">*</span></label>
                        <div class="field-element">
                            <input type="text" style="width: 100%;" v-model="form.address_1" />
                            <div class="error-messages">{{ messageFieldError('address_1') }}</div>
                        </div>
                    </div>

                    <div class="field-box">
                        <label class="field-label">Address 2</label>
                        <div class="field-element">
                            <input type="text" style="width: 100%;" v-model="form.address_2" />
                            <div class="error-messages">{{ messageFieldError('address_2') }}</div>
                        </div>
                    </div>

                    <div class="field-box">
                        <label class="field-label">City <span class="field-required">*</span></label>
                        <div class="field-element">
                            <input type="text" style="width: 100%;" v-model="form.city" />
                            <div class="error-messages">{{ messageFieldError('city') }}</div>
                        </div>
                    </div>

                    <div class="field-box">
                        <label class="field-label">State / Province <span class="field-required">*</span></label>
                        <div class="field-element">
                            <input type="text" style="width: 100%;" v-model="form.state" />
                            <div class="error-messages">{{ messageFieldError('state') }}</div>
                        </div>
                    </div>

                    <div class="field-box">
                        <label class="field-label">Postal Code <span class="field-required">*</span></label>
                        <div class="field-element">
                            <input type="text" style="width: 100%;" v-model="form.postal_code" />
                            <div class="error-messages">{{ messageFieldError('postal_code') }}</div>
                        </div>
                    </div>

                    <div class="field-box">
                        <label class="field-label">Country</label>
                        <div class="field-element">
                            <input type="text" style="width: 100%;" v-model="form.country" />
                            <div class="error-messages">{{ messageFieldError('country') }}</div>
                        </div>
                    </div>

                    <div class="field-box">
                        <label class="field-label">Email <span class="field-required">*</span></label>
                        <div class="field-element">
                            <input type="text" style="width: 100%;" v-model="form.email" />
                            <div class="error-messages">{{ messageFieldError('email') }}</div>
                        </div>
                    </div>

                    <div class="field-box">
                        <label class="field-label">Phone <span class="field-required">*</span></label>
                        <div class="field-element">
                            <input type="text" v-mask="phoneFormatMask" style="width: 100%;" v-model="form.phone" />
                            <div class="error-messages">{{ messageFieldError('phone') }}</div>
                        </div>
                    </div>

                    <div class="field-box">
                        <label class="field-label">Payment Method <span class="field-required">*</span></label>
                        <div>
                            <select v-if="availablePaymentMethods.length > 0" style="width: 100%;" v-model="form.payment_type">
                                <option v-for="method in availablePaymentMethods" :key="method.code" :value="method.code">
                                    {{ method.name }}
                                </option>
                            </select>
                            <div class="error-messages">
                                <template v-if="availablePaymentMethods.length === 0">
                                    <div style="padding-top: 8px;">Payment methods are missing or not configured. Contact your administrator</div>
                                </template>
                                <template >
                                    {{ messageFieldError('payment_type') }}
                                </template>
                            </div>
                        </div>
                    </div>

                    <div style="text-align: right; padding: 8px 0;">
                        <hr style="margin-top: 0;" />
                        <button :disabled="isLoading" type="button" @click="sendForm">Submit</button>
                    </div>
                </div>

                <div class="order-information-container">
                    <div class="title">Order information</div>

                    <table class="items-table">
                        <tr>
                            <th>Item</th>
                            <th class="quantity">Quantity</th>
                            <th style="text-align: right;">Price</th>
                        </tr>
                        <tr v-for="item in cart.items" :key="item.id">
                            <td class="info">
                                <div class="name">{{ item.product.name }}</div>
                                <div class="description" v-html="item.product.description"></div>
                            </td>
                            <td class="quantity">{{ item.quantity }}</td>
                            <td class="price">
                                <span>{{ item.quantity }} * {{ formatMoney(item.price) }} = {{ formatMoney(item.subtotal) }} {{ cart.currency }}</span><br>
                                <template v-if="item.shipping && item.shipping > 0">
                                    <span>S & H: {{ formatMoney(item.shipping) }} {{ cart.currency }}</span><br>
                                </template>
                                <span style="font-weight: bold;">Total: {{ formatMoney(item.total) }} {{ cart.currency }}</span><br>
                            </td>
                        </tr>

                        <tr>
                            <td class="total" colspan="3">
                                <span v-if="cart?.base_shipping > 0">Base Shipping Charge: {{ formatMoney(cart.base_shipping) }} {{ cart.currency }}<br></span>
                                <span v-if="cart?.subtotal > 0 && cart?.tax > 0">Subtotal: {{ formatMoney(cart.subtotal) }} {{ cart.currency }}<br></span>
                                <span v-if="cart?.tax > 0">Tax: {{ formatMoney(cart.tax) }} {{ cart.currency }}<br></span>
                                <span>Total Due: {{ formatMoney(cart.total) }} {{ cart.currency }}</span><br>
                            </td>
                        </tr>
                    </table>
                </div>
            </div>

            <template v-if="isSent">
                <div class="step-box">
                    <div class="transaction-number">Your order number is: <span class="number" @click="print">{{ result.transaction.number }}</span></div>
                    <div class="print-invoice" @click="print">Print Store Order</div>

                    <div class="checkout-title">YOUR ORDER WILL BE FULFILLED WHEN PAYMENT IS RECEIVED.</div>

                    <template v-if="result.payment_type === 'pay-pal' || result.payment_type === 'stripe'">
                        <div class="pay-button">
                            <a :href="result.url" target="_blank" style="display: block;">
                                <img src="/images/pay-button.png" alt="Pay Now" style="width: 150px; height: auto;" />
                            </a>
                        </div>
                    </template>
                </div>
            </template>
        </div>
    </section>
</template>

<script>
import EditorComponent from "./EditorComponent.vue";
import notifications from "../mixins/notifications";
import helpers from "../mixins/helpers";
import { headerOffset } from "../mixins/headerOffset";

export default {
    name: "StoreCheckoutComponent",
    mixins: [
        notifications,
        helpers,
        headerOffset
    ],
    components: {
        EditorComponent
    },
    props: {
        paymentMethods: {
            type: Object,
            default: {}
        }
    },
    data() {
        return {
            isLoading: false,
            isSent: false,
            campaigns: [],
            phoneFormatMask: '(###) ###-####',
            form: {
                first_name: '',
                last_name: '',
                address_1: '',
                address_2: '',
                country: '',
                city: '',
                state: '',
                postal_code: '',
                email: '',
                phone: '',
                payment_type: '',
                secret_name: ''
            },
            result: {
                url: '',
                payment_type: '',
                transaction: {
                    id: 0,
                    number: ''
                }
            },
            cart: {
                items: [],
                total: 0
            }
        }
    },
    computed: {
        isAuth() {
            return window.safeData.isAuth && window.safeData.isAuth === '1';
        },
        availablePaymentMethods() {
            const methods = [];

            for (const key in this.paymentMethods) {
                if (this.paymentMethods[key].enabled) {
                    methods.push({
                        code: key,
                        name: this.paymentMethods[key].name
                    });
                }
            }

            return methods;
        }
    },
    mounted() {
        this.loadAuth();
        this.loadCart();
    },
    methods: {
        loadAuth() {
            this.isLoading = true;

            this.$api.get('api/v1/auth').then(response => {
                if (response.data && response.data.is_auth) {
                    const data = response.data.user;
                    this.form.first_name = data.first_name;
                    this.form.last_name = data.last_name;
                    this.form.address_1 = data.address_1;
                    this.form.address_2 = data.address_2;
                    this.form.postal_code = data.postal_code;
                    this.form.country = data.country;
                    this.form.state = data.state;
                    this.form.city = data.city;
                    this.form.phone = data.phone;
                    this.form.email = data.email;
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        loadCart() {
            this.isLoading = true;

            this.$api.get('store/cart').then(response => {
                if (response.data) {
                    this.cart = response.data;
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        sendForm() {
            this.clearValidateErrors();

            this.isLoading = true;

            this.form.phone_length = this.phoneFormatMask.length;

            this.$api.post('store/checkout', this.form).then(response => {
                if (response.data && response.data.status) {
                    if (response.data.status === 'success') {
                        this.isSent = true;
                        this.result = response.data;
                        this.clearForm();
                    }

                    if (response.data.status === 'error') {
                        this.setValidateErrors({
                            body: [response.data.message]
                        });
                    }
                }
            }).catch((error) => {
                if (error.response && error.response.data.errors) {
                    this.setValidateErrors(error.response.data.errors);
                }
            }).finally(() => {
                this.isLoading = false;
            });

            this.$refs.startForm.scrollIntoView({ behavior: 'smooth' });
        },
        print() {
            this.isLoading = true;
            this.$api.post('store/print-invoice/' + this.result.transaction.id, {}, {
                responseType: 'blob'
            }).then(response => {
                if (response.data) {
                    const link = document.createElement('a');
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    link.href = window.URL.createObjectURL(blob);
                    window.open(link.href);
                    link.download = 'order-' + this.transaction.number + '.pdf';
                    document.body.appendChild(link);
                    // link.click();
                    document.body.removeChild(link);
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        clearForm() {
            this.form = {
                first_name: '',
                last_name: '',
                address_1: '',
                address_2: '',
                country: '',
                city: '',
                state: '',
                postal_code: '',
                email: '',
                phone: '',
                payment_type: '',
                secret_name: ''
            };

            this.cart = {
                items: [],
                total: 0
            };

            if (this.isAuth) {
                this.loadAuth();
            }
        }
    }
}
</script>

<style lang="scss">
body.modal-open {
    overflow: hidden;
}

.popup-content {
    .ck-editor__editable_inline {
        min-height: 300px!important;
    }
}
</style>

<style scoped lang="scss">
.store-section {
    max-width: 100%;
    margin: 0 auto;
    min-width: 350px;
    width: 100%;
}

.checkout-form-container {
    display: flex;

    @media (max-width: 768px) {
        flex-wrap: wrap;
    }
}

.store-title {
    font-size: 1.5em;
    text-align: center;
    margin-bottom: 16px;
}

.fields-wrap {
    min-width: 400px;
    width: 50%;
    padding-right: 32px;

    @media (max-width: 768px) {
        max-width: 100%;
        width: 100%;
        padding-right: 0;
    }
}

.field-box {
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
}

.field-label {
    display: block;
}

.checkbox-label {
    display: flex;
}

.field-required {
    color: red;
}

input:not([type=radio]):not([type=checkbox]), select {
    border: 1px solid #ccced1;
    padding: 0 8px;
    height: 34px!important;
}

.error-messages {
    color: red;
    font-size: 0.8em;
    width: 100%;
}

button {
    background: #ffffff;
    min-width: 100px;
    border: 1px solid #000000;
    border-radius: 4px;
    height: 34px!important;
}

button.send {
    border-color: green;
    color: green;
}

button.send:focus, button.send:focus-visible {
    border-color: green!important;
}

button:hover {
    background: #f0f0f0;
}

.popup {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 99999;
}

.popup-content {
    background-color: #fff;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 28px 20px 20px 20px;
    border-radius: 5px;
    overflow-y: auto;
    width: 100%;
    max-height: 90vh;
}

.checkout-title {
    font-size: 1.4em;
    margin-bottom: 16px;
    color: green;
    text-align: center;
}

.transaction-number {
    font-size: 1.2em;
    margin-bottom: 16px;
    text-align: center;
}

.number {
    cursor: pointer;
    font-weight: bold;
}

.print-invoice {
    font-size: 1.4em;
    margin-bottom: 16px;
    text-align: center;
    font-weight: bold;
    cursor: pointer;
}

.pay-title {
    font-size: 1.2em;
    margin-bottom: 16px;
    text-align: center;
}

.pay-button {
    text-align: center;
    margin-bottom: 16px;
}

.order-information-container {
    overflow-x: auto;
    flex-grow: 1;

    @media (max-width: 768px) {
        max-width: 100%;
        width: 100%;
        padding: 24px 0 0 0;
    }

    .title {
        font-size: 1.2em;
        font-weight: bold;
        margin-bottom: 24px;
    }

    .items-table {
        th {
            font-weight: bold;
            border-bottom: 1px solid #ccc;
            padding: 8px 16px;
        }

        td {
            border-bottom: 1px solid #ccc;
            padding: 8px 16px;
            vertical-align: top;
        }

        .info {
            min-width: 200px;
        }

        .quantity {
            width: 100px;
            min-width: 100px;
            text-align: center;
        }

        .price {
            width: 200px;
            min-width: 150px;
            font-size: 0.8em;
            text-align: right;
        }

        .total {
            font-weight: bold;
            text-align: right;
        }
    }
}
</style>
